import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GridColumns, GridRowIdGetter, GridRowParams } from '@mui/x-data-grid';
import { CellContentTooltip } from '../CellContentTooltip/CellContentTooltip';
import { Chip } from '@mui/material';
import { LicenseExpiry } from '../LicenseExpiry/LicenseExpiry';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { dateFormat, DateFormat } from '../../helpers/dateFormat';
import { getEnumIsFilterOperator, getEnumIsNotFilterOperator } from '../../filterOperators/enumFilterOperator';
import { RowButtonsContainer } from '../RowButtonsContainer/RowButtonsContainer';
import Button from '@mui/material/Button';
import { Link, useLocation } from 'react-router-dom';
import { getBoolFilterOperator } from '../../filterOperators/boolFilterOperator';
import { Trans, useTranslation } from 'react-i18next';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { ELicenseMode, ILicense } from '../../models/ILicense';
import { createStatusChipComponent } from '../StatusChip/StatusChip';
import styled from '@mui/styles/styled';
import { getLicenseStatus } from '../../helpers/licenseStatus';
import { LazyUser, LazyUserContextProvider } from '../LazyData/LazyUser';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { apiLicense } from '../../resources/api/apiLicense';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { AssignLicenseModal } from '../AssignLicenseModal/AssignLicenseModal';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import { OfflineAccessStepsDialog } from '../Licenses/OfflineAccessStepsDialog';
import { LicenseContext } from '../../contexts/LicenseContext';
import { ReleaseLicenseStepsDialog } from '../Licenses/ReleaseLicenseStepsDialog';
import { LicensesContext } from '../../contexts/LicensesContext';
import { LicenseDetailsModal } from '../LicenseDetailsModal/LicenseDetailsModal';
import { licenseResources } from '../../resources/builders/licenseResources';
import { useResourceChecker } from '../../resources/resource';
import { useFreeTextFilter } from '../../hooks/useFreeTextFilter';
import { apiUserDownloads } from '../../resources/api/apiUserDownloads';
import { useQueryUrl } from '../../hooks/useQueryUrl';
import { TLazyUserInfo } from '../../contexts/LazyUsersContext';
import Tooltip from '@mui/material/Tooltip';
import { getDateAfterFilterOperator, getDateBeforeFilterOperator } from '../../filterOperators/dateFilterOperators';

export interface ILicenseTableProps {
  showAssignedTo?: boolean;
  showId?: boolean;
}

// For airgap mode we need to fetch license while it is being updated on the server
const RETRY_COUNT = 5;
const RETRY_DELAY = 1000;

const StatusRenderer = createStatusChipComponent({
  valid: ['common.licenseStatus.valid', 'lime'],
  suspended: ['common.licenseStatus.suspended', 'red'],
  expiresSoon: ['common.licenseStatus.expiresSoon', 'orange'],
  expired: ['common.licenseStatus.expired', 'default'],
});

const NotInUse = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const LicenseModeChip = createStatusChipComponent({
  [ELicenseMode.Online]: ['common.licenseMode.online', 'lime'],
  [ELicenseMode.Airgap]: ['common.licenseMode.offline', 'blue'],
  [ELicenseMode.Offline]: ['common.licenseMode.offline', 'blue'],
  [ELicenseMode.Unused]: ['common.licenseMode.notInUse', 'default'],
  [ELicenseMode.Unassigned]: ['common.licenseMode.unassigned', 'default'],
  [ELicenseMode.Error]: ['common.licenseMode.error', 'red'],
});

const { useUnassignLicense, useAssignLicense, useReleaseOnline, useGetLicenseDetails } = apiLicense;
const { useGetFile } = apiUserDownloads;

export const LicenseButtons: FC<{
  license: ILicense;
}> = ({ license }) => {
  const { t } = useTranslation();

  const downloadUrl = useQueryUrl({ license: license.id?.toString() ?? null, action: 'download' });
  const unassignUrl = useQueryUrl({ license: license.id?.toString() ?? null, action: 'unassign' });
  const releaseUrl = useQueryUrl({ license: license.id?.toString() ?? null, action: 'release' });
  const assignUrl = useQueryUrl({ package: license.packageId.toString(), action: 'assign' });

  const { organization } = useContext(OrganizationContext);

  const { Guard: GuardAssign } = useAssignLicense(organization ?? void 0, 'any');
  const { Guard: GuardUnassign } = useUnassignLicense(organization ?? void 0, license.userUuid, license.id);
  const { Guard: GuardRelease } = useReleaseOnline(organization ?? void 0, license.userUuid, license.id);
  const { Guard: GuardDownload } = useGetFile();

  return (
    <RowButtonsContainer>
      <GuardDownload>
        {license.mode === ELicenseMode.Airgap ? (
          <Button component={Link} to={downloadUrl} variant="outlined" color="primary" tabIndex={-1}>
            {t('licenses.table.downloadButton')}
          </Button>
        ) : null}
      </GuardDownload>

      {license.mode === ELicenseMode.Unused ? (
        <GuardUnassign>
          <Button component={Link} to={unassignUrl} variant="outlined" color="primary" tabIndex={-1}>
            {t('licenses.table.unassignButton')}
          </Button>
        </GuardUnassign>
      ) : null}

      <GuardRelease>
        {[ELicenseMode.Online, ELicenseMode.Offline, ELicenseMode.Airgap].includes(license.mode) ? (
          <Button component={Link} to={releaseUrl} variant="outlined" color="primary" tabIndex={-1}>
            {t('licenses.table.releaseButton')}
          </Button>
        ) : null}
      </GuardRelease>

      {license.mode === ELicenseMode.Unassigned ? (
        <GuardAssign>
          <Button component={Link} to={assignUrl} variant="outlined" color="primary" tabIndex={-1}>
            {t('licenses.table.assignButton')}
          </Button>
        </GuardAssign>
      ) : null}
    </RowButtonsContainer>
  );
};

const closeDialogOpts = { replace: false };

const RenderUser: FC<{ user: TLazyUserInfo }> = ({ user }) => {
  return (
    <span>
      {user.first_name} {user.last_name}
    </span>
  );
};

const LicenseModals: FC = () => {
  const { t } = useTranslation();

  const { organization } = useContext(OrganizationContext);
  const { licenses, onRefresh } = useContext(LicensesContext);

  const handleError = useApiErrorHandler();

  const licenseId = parseInt(useQueryParam('license') ?? '');
  const packageId = parseInt(useQueryParam('package') ?? '');
  const action = useQueryParam('action');
  const mode = useQueryParam('mode');

  const closeDialog = useCloseDialog(void 0, ['license', 'package', 'action'], closeDialogOpts);

  const license = licenseId ? licenses?.find(({ id }) => id === licenseId) : null;
  const availablePackage = packageId ? licenses?.find((l) => !l.userUuid && l.packageId === packageId) : null;

  const { execute: unassign } = useUnassignLicense(organization ?? void 0, license?.userUuid, license?.id);
  const { Guard: GuardDetails } = useGetLicenseDetails(organization ?? void 0, license?.userUuid, license?.id);

  const onUnassign = useCallback(async () => {
    try {
      await unassign();
      await onRefresh();
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [unassign]);

  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setRetryCount(0);
  }, [licenseId]);

  useEffect(() => {
    if (mode === ELicenseMode.Airgap && license && license.mode !== ELicenseMode.Airgap && retryCount < RETRY_COUNT) {
      const timer = setTimeout(async () => {
        await onRefresh();
        setRetryCount((prev) => prev + 1);
      }, RETRY_DELAY);

      return () => clearTimeout(timer);
    }
  }, [mode, license, retryCount, onRefresh]);

  if (!organization) return null;

  return license ? (
    <LicenseContext.Provider value={license}>
      <LazyUserContextProvider id={license.userUuid}>
        <ConfirmationDialog
          open={action === 'unassign' && !!license.userUuid}
          title={t('customerUser.packages.unassignDialog.title')}
          message={
            <Trans
              i18nKey={'customerUser.packages.unassignDialog.message'}
              values={{
                license: license.name,
              }}
              components={{
                user: license.userUuid ? <LazyUser id={license.userUuid} Render={RenderUser} /> : null,
              }}
            />
          }
          confirm={onUnassign}
          abort={closeDialog}
        />

        <OfflineAccessStepsDialog
          open={action === 'download'}
          onClose={closeDialog}
          isLoading={mode === ELicenseMode.Airgap && license.mode !== ELicenseMode.Airgap && retryCount < 5}
        />

        <ReleaseLicenseStepsDialog open={action === 'release'} onClose={closeDialog} />

        <GuardDetails>
          {action === 'details' ? <LicenseDetailsModal isOpen={true} onClose={closeDialog} /> : null}
        </GuardDetails>
      </LazyUserContextProvider>
    </LicenseContext.Provider>
  ) : availablePackage ? (
    <AssignLicenseModal
      isOpen={action === 'assign'}
      closeDialog={closeDialog}
      organization={organization}
      license={availablePackage}
      onRefresh={onRefresh}
    />
  ) : null;
};

/**
 * @description This component needs to be rendered inside LicensesContext, OrganizationContext and UserContext
 */
export const LicensesTable: FC<ILicenseTableProps> = ({ showAssignedTo = true, showId = false }) => {
  const { licenses } = useContext(LicensesContext);
  const { organization } = useContext(OrganizationContext);

  const { search } = useLocation();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const rows = useMemo(() => {
    let index = 0;
    return (licenses ?? []).map((license) => ({
      ...license,
      status: getLicenseStatus(license.suspended, license.expiresAt, license.planType),
      inUseOn: license.machineName || t('common.notInUse'),
      index: ++index,
    }));
  }, [licenses]);

  const filterField = useQueryParam('filterField');
  const filterVal = useQueryParam('filterVal');

  const filteredRowsByShowInvalid = useMemo(() => {
    /**
     * 🚨 Hacky Alert
     * In Licenses table we usually want to display valid items only. The only exception is when user explicitly
     * demands to see expired or suspended ones.
     * In following code we check if filtering is being enabled on `status` field. If so, it means user has requested
     * to manually choose which licenses should be displayed, and therefore we should no longer hide invalid licenses.
     */
    const shouldIncludeInvalid = filterField === 'status' && filterVal;

    if (shouldIncludeInvalid) {
      return rows;
    }

    return rows.filter((row) => row.status !== 'expired' && row.status !== 'suspended');

    return rows;
  }, [rows, filterField, filterVal]);

  const columns = useMemo<GridColumns>(
    () => [
      ...(showId
        ? [
            {
              field: 'id',
              headerName: t('common.tableHeader.id'),
              width: 100,
            },
          ]
        : []),
      {
        field: 'name',
        headerName: t('common.tableHeader.license'),
        flex: 2,
        renderCell: ({ value, row }) => (
          <CellContentTooltip>
            {row.isAddon ? <Chip color="info" label={t('assignLicenses.addonChip')} /> : null} {value}
          </CellContentTooltip>
        ),
      },
      {
        field: 'userUuid',
        headerName: t('common.tableHeader.assignedTo'),
        flex: 1,
        hide: !showAssignedTo,
        hideable: showAssignedTo,
        renderCell: ({ row, value }) => {
          const el = value ? <LazyUser id={value} /> : null;
          if (row.assignedAt && el) {
            return (
              <Tooltip
                title={t('licenses.table.assignedAt', {
                  value: dateFormat(row.assignedAt, DateFormat.DateFullWithTimeAndZone, language),
                })}
              >
                <span>{el}</span>
              </Tooltip>
            );
          }
          return el;
        },
      },
      showAssignedTo
        ? {
            field: 'createdAt',
            headerName: t('common.tableHeader.created'),
            width: 120,
            valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
            filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
          }
        : {
            field: 'assignedAt',
            headerName: t('licenses.table.assignedHeader'),
            width: 120,
            valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
            filterOperators: [getDateBeforeFilterOperator(), getDateAfterFilterOperator()],
          },
      {
        field: 'expiresAt',
        headerName: t('common.tableHeader.expires'),
        width: 190,
        renderCell: ({ row }) => {
          return <LicenseExpiry type={row.planType} expiry={row.expiresAt || row.updatesExpiresAt} />;
        },
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
      },
      {
        field: 'status',
        headerName: t('common.tableHeader.status'),
        width: 120,
        renderCell: StatusRenderer,
        filterOperators: [
          getEnumIsFilterOperator({
            [t('common.licenseStatus.valid')]: ['valid', 'expiresSoon'],
            [t('common.licenseStatus.expiresSoon')]: ['expiresSoon'],
            [t('common.licenseStatus.expired')]: ['expired'],
            [t('common.licenseStatus.suspended')]: ['suspended'],
          }),
          getEnumIsNotFilterOperator({
            [t('common.licenseStatus.valid')]: ['valid', 'expiresSoon'],
            [t('common.licenseStatus.expiresSoon')]: ['expiresSoon'],
            [t('common.licenseStatus.expired')]: ['expired'],
            [t('common.licenseStatus.suspended')]: ['suspended'],
          }),
        ],
      },
      {
        field: 'inUseOn',
        headerName: t('common.tableHeader.inUseOn'),
        width: 140,
        renderCell: (params) => {
          if (params.value === t('common.notInUse')) return <NotInUse>{params.value}</NotInUse>;
          return <CellContentTooltip>{params.value}</CellContentTooltip>;
        },
      },
      {
        field: 'mode',
        headerName: t('common.tableHeader.mode'),
        width: 120,
        filterOperators: [
          getEnumIsFilterOperator({
            [t('common.licenseMode.notInUse')]: [ELicenseMode.Unused],
            [t('common.licenseMode.online')]: [ELicenseMode.Online],
            [t('common.licenseMode.offline')]: [ELicenseMode.Offline, ELicenseMode.Airgap],
          }),
          getEnumIsNotFilterOperator({
            [t('common.licenseMode.notInUse')]: [ELicenseMode.Unused],
            [t('common.licenseMode.online')]: [ELicenseMode.Online],
            [t('common.licenseMode.offline')]: [ELicenseMode.Offline, ELicenseMode.Airgap],
          }),
        ],
        renderCell: LicenseModeChip,
      },
      {
        field: 'buttons',
        headerName: t('common.tableHeader.buttons'),
        renderHeader: () => ' ',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          return (
            <LazyUserContextProvider id={params.row.userUuid}>
              <LicenseButtons license={params.row} />
            </LazyUserContextProvider>
          );
        },
        width: 280,
      },
      {
        field: 'isAddon',
        headerName: t('licenses.filter.isAddon'),
        hide: true,
        hideable: false,
        sortable: false,
        filterOperators: [getBoolFilterOperator()],
      },
    ],
    [t, showAssignedTo]
  );

  const getRowId: GridRowIdGetter = useCallback((row) => {
    return `${row.customerProductId}/${row.planId}/${row.packageId}/${row.expiresAt}/${row.id}/${row.index}`;
  }, []);

  const resourceChecker = useResourceChecker();

  const getRowLink = useCallback(
    (params: GridRowParams) => {
      if (!params.row.id || !organization) {
        return null;
      }
      const resource = licenseResources.getLicenseDetails(organization, params.row.userUuid, params.row.id);
      if (!resourceChecker(resource)) {
        return null;
      }

      const newSearch = new URLSearchParams(search);
      newSearch.set('license', params.row.id.toString());
      newSearch.set('action', 'details');
      return `?${newSearch}`;
    },
    [organization, resourceChecker, search]
  );

  const filteredRows = useFreeTextFilter(filteredRowsByShowInvalid ?? [], columns);

  if (!licenses) return null;

  return (
    <div>
      <UrlQueryDataGrid
        columns={columns}
        rows={filteredRows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        getRowId={getRowId}
        getRowLink={getRowLink}
      />

      <LicenseModals />
    </div>
  );
};
